import { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useFormikContext, useField } from 'formik';
import {
  Input,
  Select,
  Fieldset,
  CheckboxInput,
  RadioInput,
  InputError,
  AddressInput,
  SPACINGS,
  CardSelect,
  PasswordInput,
} from '@figure/figure-frontlib-gui';

const FormikCardSelect = (props) => {
  const [field] = useField(props);
  return <CardSelect {...props} {...field} />;
};

const CardSelectContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACINGS.M}px;

  & > * {
    flex: 1 1 ${({ isColumnLayout }) => (isColumnLayout ? '100%' : '250px')};
  }
`;

const FormElementWrapper = styled.div`
  flex: 1 1 ${({ isFullWidth }) => (isFullWidth ? '100%' : '300px')};
`;
const FormElementSpacer = styled.div`
  flex: 1 1 300px;
`;

const ElementGroupContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACINGS.L}px;
  flex: 1 1 100%;
  width: 100%;

  & > * {
    flex: 1 1 248px;
  }
`;

export const DynamicFormElement = ({
  formElement: {
    name,
    formElementComponentType,
    options,
    isNotShown,
    isColumnLayout,
    content,
    formElements,
    isFullWidth,
    props,
  },
  formElementIndex,
}) => {
  const { touched, errors } = useFormikContext();
  if (isNotShown) return null;
  switch (formElementComponentType) {
    case 'input':
      return (
        <FormElementWrapper isFullWidth={isFullWidth}>
          <Input
            name={name}
            {...(props?.type === 'date' && {
              placeholder: 'MM/DD/YYYY',
            })}
            {...props}
          />
        </FormElementWrapper>
      );
    case 'passwordInput':
      return (
        <FormElementWrapper isFullWidth={isFullWidth}>
          <PasswordInput name={name} {...props} />
        </FormElementWrapper>
      );
    case 'select':
      return (
        <FormElementWrapper isFullWidth={isFullWidth}>
          <Select name={name} placeholder="Select option" {...props}>
            <option value="" disabled />
            {options.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </FormElementWrapper>
      );
    case 'checkbox':
      return (
        <FormElementWrapper isFullWidth>
          <CheckboxInput name={name} {...props}>
            {props?.label}
          </CheckboxInput>
        </FormElementWrapper>
      );
    case 'cardSelect':
      return (
        <FormElementWrapper isFullWidth={isFullWidth}>
          <Fieldset legend={props?.label || ' '} errorFieldNames={[name]}>
            <CardSelectContainer isColumnLayout={isColumnLayout}>
              {options.map(({ value, label, icon }) => (
                <FormikCardSelect
                  type="radio"
                  key={value}
                  name={name}
                  id={value}
                  label={label}
                  value={value}
                  icon={icon}
                />
              ))}
            </CardSelectContainer>
            <InputError errorText={touched[name] && errors[name]} />
          </Fieldset>
        </FormElementWrapper>
      );
    case 'radio':
      return (
        <FormElementWrapper isFullWidth={isFullWidth}>
          <RadioInput
            name={name}
            radios={options}
            layout="column"
            errorFieldNames={[name]}
            {...props}
          />
        </FormElementWrapper>
      );
    case 'address':
      return (
        <FormElementWrapper isFullWidth>
          <AddressInput name={name} formikModelPath={name} legend={props?.label} {...props} />
        </FormElementWrapper>
      );
    case 'spacer':
      return <FormElementSpacer />;
    case 'content':
      return <Fragment>{content}</Fragment>;
    case 'group':
      return (
        <ElementGroupContainer>
          {formElements.map((formElement, formElementIndex) => (
            <DynamicFormElement
              key={formElement.name || formElementIndex}
              formElement={formElement}
              formElementIndex={formElementIndex}
            />
          ))}
        </ElementGroupContainer>
      );
    default:
      return null;
  }
};

DynamicFormElement.propTypes = {
  formElement: PropTypes.object.isRequired,
  formElementIndex: PropTypes.number.isRequired,
};
